import { useForm } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { useCreateServicesMutation, useUpdateServicesMutation } from "../../../../../../../API/authApi";
import { useToastify } from "../../../../../../../Hooks/hooks";
import { IMyServicesForm } from "../../../../../../../Interfaces/formValue-interfaces";
import { IMyServicesRequestData } from "../../../../../../../Interfaces/request-interfaces";
import { IServices } from "../../../../../../../Interfaces/responseTypes";
import { IMyServicesProps } from "./myServices";
import useLanguagesHooks from "../../../../../../../Components/Languages/setLanguage-hooks";

const useMyServicesHook = (props: IMyServicesProps) => {
    const { data, onUpdate } = props;
    const { t } = useLanguagesHooks();
    const [create, { isSuccess: isCreated, isLoading: createLoading, isError: isCreateError }] = useCreateServicesMutation();
    const [update, { isSuccess: isUpdated, isLoading: updateLoadng, isError: isUpdateError }] = useUpdateServicesMutation();
    const { showToastSuccess, showToastError } = useToastify();

    const defaultValues: IMyServicesForm = {
        delivery: {
            is_active: false,
            min_cost: null,
        },
        pre_order: false,
        reserve: false,
    };

    const {
        register,
        control,
        formState: { errors },
        watch,
    } = useForm<IMyServicesForm>({
        mode: 'all',
        defaultValues: data?.services ?? defaultValues,
    });

    const deliveryActive = watch('delivery.is_active');
    const minCost = watch('delivery.min_cost');
    const preOrder = watch('pre_order');
    const reserve = watch('reserve');
    const minCostStr = minCost?.toString() ?? '';
    const isMinCostValid = minCostStr.trim() !== '' && !/^0\d+/.test(minCostStr) && Number(minCostStr) >= 1;
    const isDeliveryValid = !deliveryActive || (deliveryActive && isMinCostValid);

    const currentValues: IMyServicesForm = {
        delivery: {
            is_active: deliveryActive,
            min_cost: minCost,
        },
        pre_order: preOrder,
        reserve: reserve,
    };

    const areServicesEqual = (a: IServices, b: IServices): boolean => {
        return (
            a.delivery.is_active === b.delivery.is_active &&
            Number(a.delivery.min_cost) === Number(b.delivery.min_cost) &&
            a.pre_order === b.pre_order &&
            a.reserve === b.reserve
        );
    };

    const isButtonActive = useMemo(() => {
        const servicesExist = data?.services !== null;

        if (!servicesExist) {
            return (deliveryActive && isMinCostValid) || preOrder || reserve;
        }

        const hasChanges = !areServicesEqual(data.services!, currentValues);
        return hasChanges && isDeliveryValid;

    }, [deliveryActive, minCost, preOrder, reserve, data?.services, isMinCostValid, isDeliveryValid]);

    const onSave = () => {
        const payload: IMyServicesRequestData = {
            id: data.id,
            body: {
                delivery: {
                    is_active: deliveryActive,
                    min_cost: minCost,
                },
                pre_order: preOrder,
                reserve: reserve,
            },
        };
        data?.services === null ? create(payload) : update(payload);
    };

    useEffect(() => {
        if (isUpdated || isCreated) {
            onUpdate();
            showToastSuccess(t('Toast.Success.Common'));
        } else if (isUpdateError || isCreateError) {
            showToastError(t('Error.Common'));
        }
    }, [isCreated, isCreateError, isUpdated, isUpdateError]);

    return {
        onSave,
        register,
        errors,
        control,
        updateLoadng,
        createLoading,
        deliveryActive,
        isButtonActive,
    };
};

export default useMyServicesHook;
